import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tv, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useTournamentStore } from '@/store/useTournamentStore';
import { TournamentStatus } from '@/types/tournament.model';
import { cn } from '@/lib/utils';

const MAX_SELECTION = 2;

const STATUS_LABEL: Record<string, string> = {
  [TournamentStatus.OPEN]: 'Abierto',
  [TournamentStatus.IN_PROGRESS]: 'En curso',
  [TournamentStatus.FINISHED]: 'Finalizado',
  [TournamentStatus.CANCELLED]: 'Cancelado',
};

export default function TVModeSelectorPage() {
  const navigate = useNavigate();
  const { tournaments, isLoading, error, refreshTournaments } =
    useTournamentStore();
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    refreshTournaments();
  }, [refreshTournaments]);

  const eligible = useMemo(
    () =>
      tournaments.filter((t) => t.status === TournamentStatus.IN_PROGRESS),
    [tournaments],
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_SELECTION) return prev;
      return [...prev, id];
    });
  };

  const handleStart = () => {
    if (selected.length === 0) return;
    navigate(`/tv/view?ids=${selected.join(',')}`);
  };

  return (
    <div className="space-y-6">
      <section>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-bold text-gray-900">
              <Tv className="h-7 w-7" />
              Modo TV
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Solo torneos en curso. Seleccioná hasta {MAX_SELECTION} para
              mostrar en vivo en una pantalla grande.
            </p>
          </div>
          <Button
            onClick={handleStart}
            disabled={selected.length === 0}
            className="flex w-full items-center justify-center gap-2 sm:w-auto"
          >
            <Play size={18} />
            <span>
              Iniciar Modo TV
              {selected.length > 0 ? ` (${selected.length})` : ''}
            </span>
          </Button>
        </div>

        {isLoading ? (
          <div className="rounded-lg border bg-white p-8 text-center text-sm text-gray-500">
            Cargando torneos…
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700">
            {error}
          </div>
        ) : eligible.length === 0 ? (
          <div className="rounded-lg border bg-white p-8 text-center text-sm text-gray-500">
            No hay torneos en curso disponibles para Modo TV.
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {eligible.map((t) => {
              const isSelected = selected.includes(t.id);
              const atLimit =
                selected.length >= MAX_SELECTION && !isSelected;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => toggle(t.id)}
                    disabled={atLimit}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg border bg-white p-4 text-left transition-all',
                      isSelected
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-gray-200 hover:border-gray-300',
                      atLimit && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={atLimit}
                      className="mt-1 pointer-events-none"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-semibold text-gray-900">
                          {t.name}
                        </h3>
                        <span
                          className={cn(
                            'shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-800',
                          )}
                        >
                          {STATUS_LABEL[t.status] ?? t.status}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                        <span>{t.sport?.name ?? 'Deporte'}</span>
                        {t.clubs?.[0]?.name && (
                          <span>• {t.clubs[0].name}</span>
                        )}
                        {t.teams?.length > 0 && (
                          <span>• {t.teams.length} equipos</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {selected.length >= MAX_SELECTION && (
          <p className="mt-3 text-xs text-gray-500">
            Alcanzaste el máximo de {MAX_SELECTION} torneos. Desmarcá uno para
            cambiar la selección.
          </p>
        )}
      </section>
    </div>
  );
}
